import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, setDoc, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { serverTimestamp } from 'firebase/firestore';

const SignUpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem 0;
  background-color: ${({ theme }) => theme.colors.background};
`;

const SignUpForm = styled.form`
  width: 100%;
  max-width: 600px;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const BackButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-right: 1rem;
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  margin-top: 1.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FooterText = styled.p`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  background-color: #fdf0ef;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #27ae60;
  background-color: #f0fdf4;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const RequiredText = styled.div`
  color: #e74c3c;
  background-color: #fff5f5;
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  border-left: 4px solid #e74c3c;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Step = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ $active, $completed, theme }) => 
    $completed ? theme.colors.primary : 
    $active ? theme.colors.primary + '20' : 
    theme.colors.background};
  color: ${({ $active, $completed, theme }) => 
    $completed ? 'white' : 
    $active ? theme.colors.primary : 
    theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px;
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    height: 3px;
    background-color: ${({ $nextCompleted, theme }) => 
      $nextCompleted ? theme.colors.primary : theme.colors.background};
    width: 100%;
    left: 100%;
  }
  
  &:last-child::after {
    display: none;
  }
`;

const PartnerCard = styled.div`
  border: 1px solid ${({ selected, theme }) => 
    selected ? theme.colors.primary : theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  background-color: ${({ selected, theme }) => 
    selected ? theme.colors.primary + '10' : 'white'};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary + '05'};
  }
`;

const PartnerName = styled.h4`
  margin-bottom: 0.5rem;
`;

const PartnerInfo = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const PlanCard = styled.div`
  border: 1px solid ${({ selected, theme }) => 
    selected ? theme.colors.primary : theme.colors.border};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  background-color: ${({ selected, theme }) => 
    selected ? theme.colors.primary + '10' : 'white'};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.primary + '05'};
    transform: translateY(-2px);
  }
`;

const PlanName = styled.h4`
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const PlanPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const PlanCategory = styled.span`
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  text-transform: capitalize;
  margin-bottom: 1rem;
  display: inline-block;
`;

const PlanDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const PlanDuration = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [partners, setPartners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1: Plan Selection (now mandatory)
    planId: '',
    
    // Step 2: Partner Selection
    partnerId: '',
    
    // Step 3: Health Information
    age: '',
    gender: '',
    weight: '',
    height: '',
    activityLevel: '',
    dietaryRestrictions: '',
    healthGoals: '',
    
    // Step 4: Personal Information
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Check if user came from plan selection
  useEffect(() => {
    const selectedPlanId = localStorage.getItem('selectedPlanId') || location.state?.selectedPlanId;
    if (selectedPlanId) {
      setFormData(prev => ({ ...prev, planId: selectedPlanId }));
      // Load the selected plan details
      loadSelectedPlan(selectedPlanId);
    }
  }, [location.state]);
  
  // Load selected plan details
  const loadSelectedPlan = async (planId) => {
    try {
      const planDoc = await getDoc(doc(db, 'plans', planId));
      if (planDoc.exists()) {
        setSelectedPlan({ id: planDoc.id, ...planDoc.data() });
      }
    } catch (err) {
      console.error('Error loading selected plan:', err);
    }
  };
  
  // Fetch all plans for step 1
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'plans'));
        const plansList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPlans(plansList);
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    
    fetchPlans();
  }, []);
  
  // Fetch approved partners
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const partnerQuery = query(
          collection(db, 'partners'),
          where('pendingApproval', '==', false)
        );
        
        const querySnapshot = await getDocs(partnerQuery);
        const partnersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log("Fetched partners:", partnersList.length);
        setPartners(partnersList);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError('Failed to load partners. Please try again.');
      }
    };
    
    fetchPartners();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const validateCurrentStep = () => {
    switch(currentStep) {
      case 1:
        return !!formData.planId; // Plan selection is now mandatory
      case 2:
        return !!formData.partnerId;
      case 3:
        return !!formData.age && !!formData.gender && !!formData.weight && 
               !!formData.height && !!formData.activityLevel;
      case 4:
        return !!formData.firstName && !!formData.lastName && 
               !!formData.email && !!formData.password && 
               formData.password === formData.confirmPassword;
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      if (currentStep === 1) {
        setError('Please select a plan to continue. Plan selection is required.');
      } else if (currentStep === 4 && formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
      } else {
        setError('Please fill in all required fields');
      }
    }
  };
  
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError('');
  };
  
  const selectPlan = (planId) => {
    setFormData(prevState => ({
      ...prevState,
      planId
    }));
    
    const plan = plans.find(p => p.id === planId);
    setSelectedPlan(plan);
  };
  
  const selectPartner = (partnerId) => {
    setFormData(prevState => ({
      ...prevState,
      partnerId
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCurrentStep()) {
      return setError('Please fill in all required fields');
    }
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }
    
    // Extra validation to ensure plan is selected
    if (!formData.planId) {
      return setError('A plan must be selected to create an account');
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Create user with customer role
      const result = await signup(
        formData.email, 
        formData.password, 
        `${formData.firstName} ${formData.lastName}`,
        'customer'
      );
      
      if (!result || !result.user) {
        throw new Error('Failed to create user account');
      }
      
      const user = result.user;
      
      // Save user's health data
      const healthData = {
        age: formData.age,
        gender: formData.gender,
        weight: formData.weight,
        height: formData.height,
        activityLevel: formData.activityLevel,
        dietaryRestrictions: formData.dietaryRestrictions,
        healthGoals: formData.healthGoals,
        createdAt: new Date()
      };
      
      await setDoc(doc(db, 'users', user.uid, 'health', 'profile'), healthData);
      
      // Save selected plan (now mandatory)
      const planData = {
        planId: formData.planId,
        selectedAt: new Date(),
        status: 'active',
        startDate: new Date(),
        // Calculate end date based on plan duration
        endDate: selectedPlan ? new Date(Date.now() + (selectedPlan.duration * 24 * 60 * 60 * 1000)) : null
      };
      
      await setDoc(doc(db, 'users', user.uid, 'plans', formData.planId), planData);
      
      // Store current plan in user's main document for easy access
      await setDoc(doc(db, 'users', user.uid), {
        selectedPlanId: formData.planId,
        planSelectedAt: new Date()
      }, { merge: true });
      
      // Create plan history entry
      const planHistoryData = {
        planId: formData.planId,
        planName: selectedPlan?.name || 'Unknown Plan',
        planCategory: selectedPlan?.category || 'Unknown',
        planPrice: selectedPlan?.price || 0,
        planDuration: selectedPlan?.duration || 0,
        startDate: new Date(),
        endDate: selectedPlan ? new Date(Date.now() + (selectedPlan.duration * 24 * 60 * 60 * 1000)) : null,
        status: 'active',
        selectedAt: new Date(),
        isInitialPlan: true // Flag to indicate this was the signup plan
      };
      
      // Add to plan history collection
      await addDoc(collection(db, 'users', user.uid, 'planHistory'), planHistoryData);
      
      // Clean up localStorage
      localStorage.removeItem('selectedPlanId');
      
      // Connect user with selected partner
      if (formData.partnerId) {
        await setDoc(doc(db, 'users', user.uid, 'partners', formData.partnerId), {
          partnerId: formData.partnerId,
          assignedAt: new Date()
        });
        
        // Add user to partner's clients list
        await setDoc(doc(db, 'partners', formData.partnerId, 'clients', user.uid), {
          userId: user.uid,
          assignedAt: new Date()
        });
        
        // Create initial conversation between user and partner
        const conversationsRef = collection(db, 'conversations');
        const q = query(
          conversationsRef,
          where('userId', '==', user.uid),
          where('partnerId', '==', formData.partnerId)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          // Create new conversation
          const newConversationData = {
            userId: user.uid,
            partnerId: formData.partnerId,
            createdAt: serverTimestamp(),
            lastMessageTimestamp: serverTimestamp(),
            lastMessage: 'Welcome to your nutrition journey!',
            isUserTyping: false,
            isPartnerTyping: false
          };
          
          await addDoc(conversationsRef, newConversationData);
          
          // Create welcome message
          await addDoc(collection(db, 'messages'), {
            conversationId: (await getDocs(query(
              conversationsRef,
              where('userId', '==', user.uid),
              where('partnerId', '==', formData.partnerId)
            ))).docs[0].id,
            senderId: user.uid,
            receiverId: formData.partnerId,
            content: "Hello! I'm excited to start my nutrition journey with you!",
            timestamp: serverTimestamp(),
            isRead: false,
            attachments: []
          });
        }
      }
      
      // Redirect to customer dashboard
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create an account: ' + err.message);
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <>
            <Title>Choose Your Plan</Title>
            <Subtitle>Select a plan that fits your health goals</Subtitle>
            
            <RequiredText>
              <strong>⚠️ Plan selection is required</strong> - You must choose a plan to create your account
            </RequiredText>
            
            {selectedPlan && (
              <SuccessMessage>
                Plan "{selectedPlan.name}" has been selected!
              </SuccessMessage>
            )}
            
            {plans.length === 0 ? (
              <ErrorMessage>No plans available. Please contact support to create an account.</ErrorMessage>
            ) : (
              <>
                {plans.map(plan => (
                  <PlanCard 
                    key={plan.id} 
                    selected={formData.planId === plan.id}
                    onClick={() => selectPlan(plan.id)}
                  >
                    <PlanCategory>{plan.category}</PlanCategory>
                    <PlanName>{plan.name}</PlanName>
                    <PlanPrice>${plan.price}</PlanPrice>
                    <PlanDescription>{plan.description}</PlanDescription>
                    <PlanDuration><strong>Duration:</strong> {plan.duration} days</PlanDuration>
                  </PlanCard>
                ))}
              </>
            )}
            
            <ButtonGroup>
              <Button 
                type="button" 
                onClick={handleNext}
                disabled={!formData.planId || plans.length === 0}
                style={{ width: '100%' }}
              >
                Continue with Selected Plan
              </Button>
            </ButtonGroup>
          </>
        );
        
      case 2:
        return (
          <>
            <Title>Choose Your Partner</Title>
            <Subtitle>Select a healthcare partner who will guide you on your journey</Subtitle>
            
            {partners.length === 0 ? (
              <ErrorMessage>No partners available. Please try again later.</ErrorMessage>
            ) : (
              <>
                {partners.map(partner => (
                  <PartnerCard 
                    key={partner.id} 
                    selected={formData.partnerId === partner.id}
                    onClick={() => selectPartner(partner.id)}
                  >
                    <PartnerName>{partner.displayName}</PartnerName>
                    {partner.specialization && (
                      <PartnerInfo>Specialization: {partner.specialization}</PartnerInfo>
                    )}
                    {partner.yearsExperience && (
                      <PartnerInfo>Experience: {partner.yearsExperience} years</PartnerInfo>
                    )}
                  </PartnerCard>
                ))}
              </>
            )}
            
            <ButtonGroup>
              <BackButton type="button" onClick={handleBack}>
                Back
              </BackButton>
              <Button 
                type="button" 
                onClick={handleNext}
                disabled={!formData.partnerId}
              >
                Next
              </Button>
            </ButtonGroup>
          </>
        );
        
      case 3:
        return (
          <>
            <Title>Health Information</Title>
            <Subtitle>Tell us about your health goals and current status</Subtitle>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="age">Age</Label>
                <Input
                  id="age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  min="18"
                  max="120"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="gender">Gender</Label>
                <Select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </Select>
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  id="weight"
                  name="weight"
                  type="number"
                  value={formData.weight}
                  onChange={handleChange}
                  required
                  step="0.1"
                  min="30"
                  max="300"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  id="height"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleChange}
                  required
                  min="100"
                  max="250"
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="activityLevel">Activity Level</Label>
              <Select
                id="activityLevel"
                name="activityLevel"
                value={formData.activityLevel}
                onChange={handleChange}
                required
              >
                <option value="">Select activity level</option>
                <option value="sedentary">Sedentary (little or no exercise)</option>
                <option value="light">Lightly active (light exercise/sports 1-3 days/week)</option>
                <option value="moderate">Moderately active (moderate exercise/sports 3-5 days/week)</option>
                <option value="very">Very active (hard exercise/sports 6-7 days a week)</option>
                <option value="extra">Extra active (very hard exercise & physical job or training twice a day)</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="dietaryRestrictions">Dietary Restrictions (Optional)</Label>
              <Select
                id="dietaryRestrictions"
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={handleChange}
              >
                <option value="">None</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="vegan">Vegan</option>
                <option value="gluten-free">Gluten-free</option>
                <option value="dairy-free">Dairy-free</option>
                <option value="keto">Keto</option>
                <option value="paleo">Paleo</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="healthGoals">Health Goals</Label>
              <Select
                id="healthGoals"
                name="healthGoals"
                value={formData.healthGoals}
                onChange={handleChange}
                required
              >
                <option value="">Select your primary goal</option>
                <option value="weight-loss">Weight loss</option>
                <option value="muscle-gain">Muscle gain</option>
                <option value="maintenance">Weight maintenance</option>
                <option value="health-improvement">General health improvement</option>
                <option value="athletic-performance">Athletic performance</option>
              </Select>
            </FormGroup>
            
            <ButtonGroup>
              <BackButton type="button" onClick={handleBack}>
                Back
              </BackButton>
              <Button 
                type="button" 
                onClick={handleNext}
                disabled={!formData.age || !formData.gender || !formData.weight || !formData.height || !formData.activityLevel}
              >
                Next
              </Button>
            </ButtonGroup>
          </>
        );
        
      case 4:
        return (
          <>
            <Title>Create Your Account</Title>
            <Subtitle>Complete your registration to get started</Subtitle>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <ButtonGroup>
              <BackButton type="button" onClick={handleBack}>
                Back
              </BackButton>
              <Button 
                type="submit" 
                disabled={loading || !formData.planId}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </ButtonGroup>
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <SignUpContainer>
      <SignUpForm onSubmit={handleSubmit}>
        <StepIndicator>
          <Step $active={currentStep === 1} $completed={currentStep > 1} $nextCompleted={currentStep > 1}>1</Step>
          <Step $active={currentStep === 2} $completed={currentStep > 2} $nextCompleted={currentStep > 2}>2</Step>
          <Step $active={currentStep === 3} $completed={currentStep > 3} $nextCompleted={currentStep > 3}>3</Step>
          <Step $active={currentStep === 4} $completed={false}>4</Step>
        </StepIndicator>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {renderStep()}
        
        <FooterText>
          Already have an account? <StyledLink to="/auth/login">Log In</StyledLink>
        </FooterText>
      </SignUpForm>
    </SignUpContainer>
  );
};

export default SignUp;